import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Form, Input, Select, Button, Card, message, Spin, Modal, Typography, Divider, Alert, Steps, Breadcrumb, App } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../../utils/api';
import { theme } from '../../theme';
import { ExclamationCircleOutlined, HomeOutlined, UserOutlined as UserOutlinedIcon } from '@ant-design/icons';

const { Text } = Typography;

const { Option } = Select;

// Interfaces para los datos
interface Provincia {
  id: number;
  provincia: string;
}

interface Localidad {
  id: number;
  localidad: string;
  departamento: string;
  departamento_id: number;
}

interface TipoDocumento {
  id: number;
  descripcion: string;
}

interface CategoriaIva {
  id: number;
  civa: string;
}

interface Actividad {
  id: number;
  actividad: string;
}

interface FormValues {
  denominacion: string;
  direccion: string;
  provincia_id: number;
  localidad_id: number;
  departamento: string;
  tipo_documento_id: number;
  nro_documento: string;
  telefono: string;
  email: string;
  categoria_iva_id: number;
  actividad_id: number;
  departamento_id: number;
}

interface AltaPadronProps {
  open?: boolean;
  onCancel?: () => void;
  onSuccess?: () => void;
  asModal?: boolean;
}

const AltaPadron: React.FC<AltaPadronProps> = ({ open, onCancel, onSuccess, asModal = false }) => {
  const [form] = Form.useForm();
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);
  const [provincias, setProvincias] = useState<Provincia[]>([]);
  const [localidades, setLocalidades] = useState<Localidad[]>([]);
  const [tiposDocumento, setTiposDocumento] = useState<TipoDocumento[]>([]);
  const [categoriasIva, setCategoriasIva] = useState<CategoriaIva[]>([]);
  const [actividades, setActividades] = useState<Actividad[]>([]);
  const [departamento, setDepartamento] = useState('');
  const [userData, setUserData] = useState<any>(null);
  const [documentoDuplicado, setDocumentoDuplicado] = useState(false);
  const [padronDuplicadoInfo, setPadronDuplicadoInfo] = useState<{ nc: string; denominacion: string } | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveAndNew, setSaveAndNew] = useState(false);
  const initialValuesRef = useRef<any>(null);

  useEffect(() => {
    const fetchCatalogos = async () => {
      try {
        const token = await getAccessTokenSilently();
        
        console.log('Iniciando carga de catálogos...');

        // Realizar todas las llamadas en paralelo
        const [
          provinciasResponse,
          tiposDocResponse,
          tiposRespResponse,
          actividadesResponse
        ] = await Promise.all([
          // Provincias
          apiClient.get<Provincia[]>(
            `${process.env.REACT_APP_COMMON_URL}/listar_provincias`,
            { token }
          ),
          // Tipos de Documento
          apiClient.get<TipoDocumento[]>(
            `${process.env.REACT_APP_COMMON_URL}/listar_tipodocumento`,
            { token }
          ),
          // Tipos de Responsable (IVA)
          apiClient.get<CategoriaIva[]>(
            `${process.env.REACT_APP_COMMON_URL}/listar_tiporesponsable`,
            { token }
          ),
          // Actividades
          apiClient.get<Actividad[]>(
            `${process.env.REACT_APP_COMMON_URL}/listar_actividades`,
            { token }
          )
        ]);

        console.log('Provincias cargadas:', provinciasResponse.data.length);
        console.log('Tipos de documento cargados:', tiposDocResponse.data.length);
        console.log('Tipos de responsable cargados:', tiposRespResponse.data.length);
        console.log('Actividades cargadas:', actividadesResponse.data.length);

        // Actualizar los estados con los datos recibidos
        setProvincias(provinciasResponse.data);
        setTiposDocumento(tiposDocResponse.data);
        setCategoriasIva(tiposRespResponse.data);
        setActividades(actividadesResponse.data);

      } catch (error) {
        console.error('Error al cargar catálogos:', error);
        message.error('Error al cargar datos iniciales. Por favor, recargue la página.');
      } finally {
        setLoadingCatalogos(false);
      }
    };

    fetchCatalogos();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (user) {
      setUserData({
        id_empresa: user?.id_empresa,
        id_sucursal: user?.id_sucursal
      });
    }
  }, [user]);

  // Inicializar valores iniciales cuando el formulario está listo
  useEffect(() => {
    if (!loadingCatalogos) {
      const initialValues = form.getFieldsValue();
      initialValuesRef.current = initialValues;
    }
  }, [loadingCatalogos, form]);

  // Detectar cambios sin guardar
  useEffect(() => {
    const unsubscribe = form.getFieldsValue();
    const checkChanges = () => {
      const currentValues = form.getFieldsValue();
      const hasChanges = JSON.stringify(currentValues) !== JSON.stringify(initialValuesRef.current);
      setHasUnsavedChanges(hasChanges);
    };

    form.getFieldsError();
    const timer = setInterval(checkChanges, 500);
    return () => clearInterval(timer);
  }, [form]);

  // Confirmar antes de salir si hay cambios sin guardar
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Validar documento duplicado
  const validarDocumentoDuplicado = async (nroDocumento: string, tipoDocumentoId: number) => {
    // Excepciones: permitir 00000 y 99999
    const documentoNormalizado = nroDocumento.replace(/\D/g, '');
    if (documentoNormalizado === '00000000000' || documentoNormalizado === '99999999999') {
      setDocumentoDuplicado(false);
      return Promise.resolve();
    }

    try {
      const token = await getAccessTokenSilently();
      if (!userData?.id_empresa) return Promise.resolve();

      // Buscar si existe el documento
      const response = await apiClient.get(
        `${process.env.REACT_APP_PADRON_URL}/buscar_padron/${userData.id_empresa}/`,
        { token }
      );

      const duplicado = response.data.find((record: any) =>
        record.ndocumento === documentoNormalizado
      );

      if (duplicado) {
        setDocumentoDuplicado(true);
        setPadronDuplicadoInfo({ nc: duplicado.nc || '', denominacion: duplicado.denominacion || '' });
        return Promise.reject(new Error('Ya existe un registro con este documento'));
      } else {
        setDocumentoDuplicado(false);
        setPadronDuplicadoInfo(null);
        return Promise.resolve();
      }
    } catch (error) {
      console.error('Error al validar documento:', error);
      // Si hay error en la validación, permitir continuar
      return Promise.resolve();
    }
  };

  const handleProvinciaChange = async (provinciaId: number) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await apiClient.get<Localidad[]>(
        `${process.env.REACT_APP_COMMON_URL}/listar_localidades/${provinciaId}`,
        { token }
      );
      setLocalidades(response.data);
      form.setFieldValue('localidad_id', undefined);
    } catch (error) {
      console.error('Error al cargar localidades:', error);
      message.error('Error al cargar localidades');
    }
  };

  const handleLocalidadChange = (localidadId: number) => {
    const localidad = localidades.find(l => l.id === localidadId);
    if (localidad) {
      console.log('Localidad seleccionada:', localidad);
      // Guardamos tanto el nombre como el id del departamento
      setDepartamento(localidad.departamento);
      form.setFieldsValue({
        departamento: localidad.departamento,
        departamento_id: localidad.departamento_id
      });
    } else {
      setDepartamento('');
      form.setFieldsValue({
        departamento: '',
        departamento_id: null
      });
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: '¿Descartar cambios?',
        icon: <ExclamationCircleOutlined />,
        content: 'Tiene cambios sin guardar. ¿Está seguro de que desea salir?',
        okText: 'Sí, descartar',
        cancelText: 'Cancelar',
        okType: 'danger',
        onOk: () => {
          setHasUnsavedChanges(false);
          if (asModal && onCancel) {
            form.resetFields();
            onCancel();
          } else {
            navigate('/listado_padron');
          }
        }
      });
    } else {
      if (asModal && onCancel) {
        form.resetFields();
        onCancel();
      } else {
        navigate('/listado_padron');
      }
    }
  };

  // Calcular progreso del formulario
  const formValues = Form.useWatch([], form);
  const formProgress = useMemo(() => {
    if (!formValues) return 0;
    
    const totalFields = 11; // Total de campos requeridos
    let completedFields = 0;

    if (formValues.denominacion) completedFields++;
    if (formValues.direccion) completedFields++;
    if (formValues.provincia_id) completedFields++;
    if (formValues.localidad_id) completedFields++;
    if (formValues.tipo_documento_id) completedFields++;
    if (formValues.nro_documento && formValues.nro_documento.length >= 8) completedFields++;
    if (formValues.telefono) completedFields++;
    if (formValues.email) completedFields++;
    if (formValues.categoria_iva_id) completedFields++;
    if (formValues.actividad_id) completedFields++;
    if (formValues.departamento_id) completedFields++;

    return Math.round((completedFields / totalFields) * 100);
  }, [formValues]);

  const onFinish = async (values: FormValues, saveAndContinue: boolean = false) => {
    setLoading(true);
    try {
      if (!userData?.id_empresa || !userData?.id_sucursal) {
        message.error('No se encontraron datos de empresa/sucursal');
        return;
      }

      // Bloquear si el documento ya está registrado en esta empresa
      if (documentoDuplicado) {
        message.error('No se puede guardar: el número de documento ya está registrado en el padrón.');
        return;
      }

      const ruta = `${process.env.REACT_APP_PADRON_URL}/alta_padron/${userData.id_empresa}/${userData.id_sucursal}`;
      
      const token = await getAccessTokenSilently();
      const response = await apiClient.post(
        ruta,
        values,
        { token }
      );

      message.success({
        content: saveAndContinue ? 'Registro guardado. Puede continuar cargando otro registro.' : 'Registro guardado exitosamente',
        duration: 3,
      });
      setHasUnsavedChanges(false);
      
      if (saveAndContinue) {
        // Limpiar formulario y mantener en la página/modal
        form.resetFields();
        initialValuesRef.current = form.getFieldsValue();
        if (!asModal) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else {
        // Si es modal, cerrar y refrescar listado. Si es página, navegar.
        if (asModal && onSuccess) {
          form.resetFields();
          onSuccess();
        } else {
          setTimeout(() => {
            navigate('/listado_padron');
          }, 1000);
        }
      }
    } catch (error: any) {
      console.error('Error completo:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al guardar el registro';
      message.error({
        content: errorMessage,
        duration: 5,
      });
    } finally {
      setLoading(false);
      setSaveAndNew(false);
    }
  };

  const handleSaveAndNew = () => {
    setSaveAndNew(true);
    form.submit();
  };

  // Contenido del formulario
  const formContent = (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => onFinish(values, saveAndNew)}
        onValuesChange={() => setHasUnsavedChanges(true)}
        style={{ 
          maxWidth: '100%',
          padding: asModal ? '16px' : '16px'
        }}
        className="padron-form"
        initialValues={initialValuesRef.current}
      >
        <style>{`
          .padron-form .ant-form-item-label > label {
            color: #374151 !important;
            font-weight: 500 !important;
            font-size: 13px !important;
            line-height: 1.4 !important;
          }
          .padron-form .ant-form-item {
            margin-bottom: 0 !important;
          }
          .padron-form .ant-form-item-required::before {
            color: ${theme.colors.error} !important;
          }
          .padron-form .ant-input,
          .padron-form .ant-select-selector,
          .padron-form .ant-input-number {
            border-color: #d1d5db !important;
            border-radius: 6px !important;
          }
          .padron-form .ant-input:focus,
          .padron-form .ant-select-selector:focus,
          .padron-form .ant-input-number:focus {
            border-color: ${theme.colors.primary} !important;
            box-shadow: 0 0 0 2px rgba(45, 92, 146, 0.2) !important;
          }
        `}</style>

        {/* Alerta de cambios sin guardar */}
        {hasUnsavedChanges && (
          <Alert
            message="Tiene cambios sin guardar"
            type="warning"
            showIcon
            closable
            style={{ marginBottom: '12px' }}
            action={
              <Button size="small" onClick={() => {
                form.resetFields();
                setHasUnsavedChanges(false);
              }}>
                Descartar
              </Button>
            }
          />
        )}

        {/* Alerta de documento duplicado */}
        {documentoDuplicado && (
          <Alert
            message="Número de documento ya registrado"
            description={
              padronDuplicadoInfo
                ? `El documento ya existe en el padrón: NC ${padronDuplicadoInfo.nc} — ${padronDuplicadoInfo.denominacion}. No se puede registrar un documento duplicado.`
                : 'El número de documento ya existe en el padrón de esta empresa. No se puede registrar un documento duplicado.'
            }
            type="error"
            showIcon
            style={{ marginBottom: '12px' }}
          />
        )}
        
        {/* Layout compacto: todos los campos en una sola vista */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
          {/* Razón Social */}
          <Form.Item
            name="denominacion"
            label="Razón Social"
            rules={[{ required: true, message: 'Ingrese la razón social' }]}
            style={{ marginBottom: '16px' }}
          >
            <Input placeholder="Ej: Empresa S.A., Juan Pérez, etc." size="small" />
          </Form.Item>

          {/* Dirección */}
          <Form.Item
            name="direccion"
            label="Dirección"
            rules={[{ required: true, message: 'Ingrese la dirección' }]}
            style={{ marginBottom: '16px' }}
          >
            <Input placeholder="Ej: Av. Libertador 1234, Piso 5, Dpto. A" size="small" />
          </Form.Item>

          {/* Provincia */}
          <Form.Item
            name="provincia_id"
            label="Provincia"
            rules={[{ required: true, message: 'Seleccione la provincia' }]}
            style={{ marginBottom: '16px' }}
          >
            <Select 
              onChange={handleProvinciaChange}
              placeholder="Seleccione provincia"
              showSearch
              optionFilterProp="children"
              size="small"
            >
              {provincias.map(prov => (
                <Option key={prov.id} value={prov.id}>{prov.provincia}</Option>
              ))}
            </Select>
          </Form.Item>

          {/* Localidad */}
          <Form.Item
            name="localidad_id"
            label="Localidad"
            rules={[{ required: true, message: 'Seleccione la localidad' }]}
            style={{ marginBottom: '16px' }}
          >
            <Select 
              onChange={handleLocalidadChange}
              placeholder="Seleccione localidad"
              showSearch
              optionFilterProp="children"
              disabled={!form.getFieldValue('provincia_id')}
              size="small"
            >
              {localidades.map(loc => (
                <Option key={loc.id} value={loc.id}>{loc.localidad}</Option>
              ))}
            </Select>
          </Form.Item>

          {/* Departamento */}
          <Form.Item 
            label="Departamento"
            name="departamento"
            style={{ marginBottom: '16px' }}
          >
            <Input 
              disabled 
              placeholder="Se completa automáticamente"
              size="small"
            />
          </Form.Item>
          <Form.Item name="departamento_id" hidden>
            <Input type="hidden" />
          </Form.Item>

          {/* Tipo Documento */}
          <Form.Item
            name="tipo_documento_id"
            label="Tipo Documento"
            rules={[{ required: true, message: 'Seleccione el tipo de documento' }]}
            style={{ marginBottom: '16px' }}
          >
            <Select placeholder="Seleccione tipo de documento" size="small">
              {tiposDocumento.map(tipo => (
                <Option key={tipo.id} value={tipo.id}>{tipo.descripcion}</Option>
              ))}
            </Select>
          </Form.Item>

          {/* Nro. Documento */}
          <Form.Item
            name="nro_documento"
            label="Nro. Documento"
            validateTrigger="onBlur"
            rules={[
              { required: true, message: 'Ingrese el número de documento' },
              {
                validator: async (_, value) => {
                  if (!value) return Promise.resolve();
                  const documentoNormalizado = value.replace(/\D/g, '');
                  const tipoDocId = form.getFieldValue('tipo_documento_id');
                  const tipoDoc = tiposDocumento.find(t => t.id === tipoDocId);
                  const tipoDescripcion = (tipoDoc?.descripcion || '').toUpperCase();
                  const esCuitCuil = tipoDescripcion.includes('CUIT') || tipoDescripcion.includes('CUIL');
                  const esDni = tipoDescripcion.includes('DNI');

                  if (esCuitCuil) {
                    if (documentoNormalizado.length !== 11) {
                      return Promise.reject(new Error(`El CUIT/CUIL debe tener exactamente 11 dígitos (tiene ${documentoNormalizado.length})`));
                    }
                  } else if (esDni) {
                    if (documentoNormalizado.length < 8) {
                      return Promise.reject(new Error(`El DNI debe tener al menos 8 dígitos (tiene ${documentoNormalizado.length})`));
                    }
                    if (documentoNormalizado.length > 11) {
                      return Promise.reject(new Error('El DNI no puede tener más de 11 dígitos'));
                    }
                  } else {
                    if (documentoNormalizado.length > 11) {
                      return Promise.reject(new Error('El número de documento no puede tener más de 11 dígitos'));
                    }
                  }

                  if (tipoDocId) {
                    await validarDocumentoDuplicado(documentoNormalizado, tipoDocId);
                  }
                  return Promise.resolve();
                }
              }
            ]}
            extra={
              <Text type="secondary" style={{ fontSize: '11px', lineHeight: '1.2' }}>
                CUIT/CUIL: 11 dígitos. DNI: 8 a 11 dígitos. Se permiten 00000000000 y 99999999999.
              </Text>
            }
            style={{ marginBottom: '16px' }}
          >
            <Input
              maxLength={11}
              placeholder="Ej: 20123456789"
              onKeyPress={(e) => {
                const isNumber = /\d/.test(e.key);
                if (!isNumber) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                form.setFieldValue('nro_documento', value);
                if (documentoDuplicado) {
                  setDocumentoDuplicado(false);
                  setPadronDuplicadoInfo(null);
                }
              }}
              size="small"
            />
          </Form.Item>

          {/* Categoría IVA */}
          <Form.Item
            name="categoria_iva_id"
            label="Categoría IVA"
            rules={[{ required: true, message: 'Seleccione la categoría de IVA' }]}
            style={{ marginBottom: '16px' }}
          >
            <Select placeholder="Seleccione categoría IVA" size="small">
              {categoriasIva.map(cat => (
                <Option key={cat.id} value={cat.id}>{cat.civa}</Option>
              ))}
            </Select>
          </Form.Item>

          {/* Teléfono */}
          <Form.Item
            name="telefono"
            label="Nro. de Teléfono"
            rules={[{ required: true, message: 'Ingrese el número de teléfono' }]}
            style={{ marginBottom: '16px' }}
          >
            <Input placeholder="Ej: (011) 1234-5678" size="small" />
          </Form.Item>

          {/* Email */}
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Ingrese el email' },
              { type: 'email', message: 'Ingrese un email válido' }
            ]}
            style={{ marginBottom: '16px' }}
          >
            <Input type="email" placeholder="Ej: contacto@empresa.com.ar" size="small" />
          </Form.Item>

          {/* Actividad */}
          <Form.Item
            name="actividad_id"
            label="Actividad"
            rules={[{ required: true, message: 'Seleccione la actividad' }]}
            style={{ marginBottom: '16px' }}
          >
            <Select placeholder="Seleccione actividad" size="small">
              {actividades.map(act => (
                <Option key={act.id} value={act.id}>{act.actividad}</Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        {/* Botones de acción */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          marginTop: '16px',
          paddingTop: '16px',
          borderTop: `1px solid ${theme.colors.borderLight}`
        }}>
          <Button
            onClick={handleCancel}
            size="large"
            style={{
              fontWeight: 500,
              minWidth: '120px'
            }}
          >
            Cancelar
          </Button>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              type="default"
              onClick={handleSaveAndNew}
              loading={loading && saveAndNew}
              size="large"
              style={{
                fontWeight: 500,
                minWidth: '150px'
              }}
            >
              {loading && saveAndNew ? 'Guardando...' : 'Guardar y Nuevo'}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading && !saveAndNew}
              size="large"
              style={{
                backgroundColor: theme.colors.secondary,
                borderColor: theme.colors.secondary,
                fontWeight: 500,
                minWidth: '120px'
              }}
            >
              {loading && !saveAndNew ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>

      </Form>
    </>
  );

  // Si es modal, retornar el contenido del formulario directamente
  if (asModal) {
    return (
      <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', padding: '0' }}>
        {loadingCatalogos ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '300px',
            gap: '16px'
          }}>
            <Spin size="large" />
            <div style={{ textAlign: 'center' }}>
              <Text strong style={{ fontSize: '16px', color: theme.colors.textPrimary, display: 'block', marginBottom: '8px' }}>
                Cargando formulario...
              </Text>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                Obteniendo provincias, tipos de documento, categorías IVA y actividades
              </Text>
            </div>
          </div>
        ) : (
          <>
            {/* Indicador de progreso para modal */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              padding: '12px 16px',
              backgroundColor: theme.colors.backgroundGray,
              borderRadius: '8px',
              marginBottom: '16px',
              border: `1px solid ${theme.colors.borderLight}`
            }}>
              <Text strong style={{ fontSize: '14px', color: theme.colors.primaryDark }}>
                Nuevo Registro
              </Text>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>Progreso:</Text>
                <div style={{
                  width: '100px',
                  height: '6px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${formProgress}%`,
                    height: '100%',
                    backgroundColor: formProgress === 100 ? '#52c41a' : theme.colors.primary,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <Text strong style={{ fontSize: '12px', color: theme.colors.textPrimary, minWidth: '30px' }}>
                  {formProgress}%
                </Text>
              </div>
            </div>
            {formContent}
          </>
        )}
      </div>
    );
  }

  // Mostrar loading mientras se cargan los catálogos (modo página)
  if (loadingCatalogos) {
    return (
      <App>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px',
          gap: '16px'
        }}>
          <Spin size="large" />
          <div style={{ textAlign: 'center' }}>
            <Text strong style={{ fontSize: '16px', color: theme.colors.textPrimary, display: 'block', marginBottom: '8px' }}>
              Cargando formulario...
            </Text>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              Obteniendo provincias, tipos de documento, categorías IVA y actividades
            </Text>
          </div>
        </div>
      </App>
    );
  }

  // Modo página (comportamiento original)
  return (
    <App>
      <div style={{ padding: '0' }}>
        {/* Breadcrumb */}
        <Breadcrumb
          style={{ marginBottom: '16px' }}
          items={[
            {
              href: '/listado_padron',
              title: (
                <span>
                  <HomeOutlined />
                  <span style={{ marginLeft: '8px' }}>Padrón</span>
                </span>
              ),
            },
            {
              title: (
                <span>
                  <UserOutlinedIcon />
                  <span style={{ marginLeft: '8px' }}>Nuevo Registro</span>
                </span>
              ),
            },
          ]}
        />

        {/* Título de la página */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <h1 style={{ 
            color: theme.colors.primaryDark, 
            fontSize: '28px', 
            fontWeight: 600, 
            margin: 0
          }}>
            Nuevo Registro
          </h1>
          
          {/* Indicador de progreso */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '8px 16px',
            backgroundColor: theme.colors.backgroundGray,
            borderRadius: '8px',
            border: `1px solid ${theme.colors.borderLight}`
          }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>Progreso:</Text>
            <div style={{
              width: '120px',
              height: '8px',
              backgroundColor: '#e5e7eb',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${formProgress}%`,
                height: '100%',
                backgroundColor: formProgress === 100 ? '#52c41a' : theme.colors.primary,
                transition: 'width 0.3s ease'
              }} />
            </div>
            <Text strong style={{ fontSize: '12px', color: theme.colors.textPrimary, minWidth: '35px' }}>
              {formProgress}%
            </Text>
          </div>
        </div>

      <Card
        style={{ 
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '1200px'
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '16px',
            fontWeight: 600
          }
        }}
        title="Información del Registro"
      >
        {formContent}
    </Card>
    </div>
    </App>
  );
};

export default AltaPadron;
